import React, { useState } from 'react';
import { World } from '../../types';
import { Section } from '../ui/primitives';
import { statusInfo } from '../../utils/format';
import LiveLogs from './LiveLogs';
import HistoryLogs from './HistoryLogs';
import CrashLogs from './CrashLogs';

type Mode = 'live' | 'history' | 'crashes';

const BLURB: Record<Mode, string> = {
  live: 'Console output as it happens - visible to admins and developers only.',
  history: 'Older console output from Loki - visible to admins and developers only.',
  crashes: 'Logs, crash reports and JVM error logs copied off servers that crashed - visible to admins and developers only.',
};

/**
 * Server console for admins and developers. Live follows the running container over a WebSocket, and lets
 * developers run console commands; History queries Loki, which keeps the output of servers that are long gone;
 * Crashes shows the files copied off crashed servers before they were deleted.
 */
const LogsTab: React.FC<{ world: World; consoleAllowed?: boolean }> = ({ world, consoleAllowed }) => {
  const st = statusInfo(world.status);
  const running = st.active || st.busy;
  // Remembered per world so switching worlds lands on the sensible default again.
  const [choice, setChoice] = useState<{ id: string; mode: Mode } | null>(null);
  const mode: Mode = choice && choice.id === world.id ? choice.mode : running ? 'live' : 'history';
  const setMode = (m: Mode) => setChoice({ id: world.id, mode: m });

  return (
    <Section
      title="Server log"
      blurb={BLURB[mode]}
      actions={
        <div className="seg" role="group" aria-label="Log source">
          <button type="button" aria-pressed={mode === 'live'} onClick={() => setMode('live')} disabled={!running && mode !== 'live'} title={running ? undefined : 'The world is offline'}>
            Live
          </button>
          <button type="button" aria-pressed={mode === 'history'} onClick={() => setMode('history')}>
            History
          </button>
          <button type="button" aria-pressed={mode === 'crashes'} onClick={() => setMode('crashes')}>
            Crashes
          </button>
        </div>
      }
    >
      {mode === 'live' ? <LiveLogs world={world} consoleAllowed={consoleAllowed} /> : mode === 'history' ? <HistoryLogs world={world} /> : <CrashLogs world={world} />}
    </Section>
  );
};

export default LogsTab;
